<template>
  <div class="position: relative;">
    <q-toolbar
      color="white"
      text-color="grey-9"
      class="top-page-toolbar"
      style="position: fixed; top: calc(69 + var(--top-offset, 0))px; left: 0; z-index: 10; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);"
    >
      <q-toolbar-title></q-toolbar-title>

      <div>
        <q-btn
          :label="$t('preview')"
          color="tertiary"
          class="q-mr-md"
          flat
          @click="previewQuote"
        />

        <q-btn
          v-if="editing && !editingDraft"
          color="primary"
          @click="createQuote"
        >
          {{ $t('save') }}
        </q-btn>

        <q-btn-dropdown
          v-else
          :label="$t('create')"
          color="primary"
          split
          @click="createQuote"
        >
          <q-list link>
            <q-item v-close-overlay>
              <q-item-main>
                <q-item-tile
                  label
                  @click.native="saveDraft"
                >
                {{ $t('save_draft') }}
              </q-item-tile>
              </q-item-main>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </div>
    </q-toolbar>

    <div class="layout-padding">
      <section class="invoicing-create">
        <header>
          <img
            v-if="activeTeam.settings.logo_url"
            :src="activeTeam.settings.logo_url"
            class="logo"
          >

          <span v-html="returnLine" class="return-line"></span>

          <div class="customer">
            <customer-select
              :hasError="errors.has('customer')"
              :value="customer"
              :types="customerTypes.ALL"
              :errorLabel="errors.first('customer')"
              inverted-light
              hide-underline
              @input="handleCustomerSelect"
            />
            <customer-select
              :types="[customerTypes.CONTACT_PERSON]"
              :placeholder="$tc('contact_person', 1)"
              :companyFilter="customerId"
              :hasErrors="errors.has('contactPerson')"
              :errorLabel="errors.first('contactPerson')"
              v-model="contactPerson"
              v-if="customer && customer.type === customerTypes.COMPANY"
              class="q-mt-sm"
            />
            <customer-address-select
              v-if="customer"
              v-model="address"
              :customer="customer"
              @reFetchCustomer="handleReFetchCustomer"
              class="q-mt-lg"
            />
          </div>
        </header>

        <main>
          <div class="row items-end">
            <div class="invoice-title">
              <span v-show="!editTitle" class="headline q-my-none" @click="toggleQuoteTitle">
                {{ quoteTitle }}
              </span>

              <q-field
                v-show="editTitle"
                class="q-ma-none headline-input"
                :error="errors.has('quote_title')"
                :error-label="errors.first('quote_title')"
              >
                <q-input
                  ref="quoteTitleInput"
                  v-model="quoteTitle"
                  v-validate.disable="'required|max:255'"
                  :data-vv-as="$t('title')"
                  :placeholder="$t('title')"
                  :disable="!editTitle"
                  @blur="handleBlurQuoteTitle"
                  @keydown.enter="handleBlurQuoteTitle"
                  autofocus
                  hide-underline
                />
              </q-field>

              <q-tooltip v-show="!editTitle">
                {{ $t('click_to_edit') }}
              </q-tooltip>
            </div>

            <div class="invoice-number">
              <span
                class="invoice-number-text"
                v-show="generateQuoteNumber"
                @click="toggleQuoteNumber"
              >
                {{ nextQuoteNumber }}
              </span>

              <q-field
                v-show="!generateQuoteNumber"
                :error="errors.has('quote_number')"
                :error-label="errors.first('quote_number')"
              >
                <q-input
                  v-model="quoteNumber"
                  v-validate.disable="'max:255'"
                  name="quote_number"
                  prefix="#"
                  :data-vv-as="$t('quote_number')"
                  :placeholder="$t('quote_number')"
                  :disable="generateQuoteNumber"
                  :after="[{ icon: 'cancel', handler () { generateQuoteNumber = true; quoteNumber = null }}]"
                  @blur="checkQuoteNumber"
                  autofocus
                  hide-underline
                  ref="quoteNumberInput"
                />
              </q-field>

              <q-tooltip v-show="generateQuoteNumber">
                {{ $t('invoice_number_tooltip') }}
              </q-tooltip>
            </div>
          </div>

          <div class="invoice-meta q-mt-sm">
            <q-field
              :error="errors.has('quoteDate')"
              :error-label="errors.first('quoteDate')"
              :label="$t('quote_date')"
              :label-width="6"
              class="q-ma-none"
            >
              <q-datetime
                v-model="quoteDate"
                type="date"
                v-validate.disable="'required'"
                name="quoteDate"
                hide-underline
                :data-vv-as="$t('quote_date')"
                :placeholder="$t('quote_date')"
                :format="$t('date_format')"
              />
            </q-field>

            <q-field
              :error="errors.has('validUntil')"
              :error-label="errors.first('validUntil')"
              :label="$t('valid_until')"
              :label-width="6"
              class="q-ma-none"
            >
              <q-datetime
                v-model="validUntil"
                type="date"
                name="validUntil"
                v-validate.disable="'required'"
                :data-vv-as="$t('valid_until')"
                :placeholder="$t('valid_until')"
                :format="$t('date_format')"
                hide-underline
              />
            </q-field>

            <q-field
              :label="$tc('project', 1)"
              :label-width="6"
              class="q-ma-none"
            >
              <div>
                <project-indicator :project="project ? project : undefined" clickable />
                <project-popover
                    :active-project="project ? project : undefined"
                    @select-project="handleSelectProject"
                  />
              </div>
            </q-field>
          </div>

          <add-textarea-box
            v-model="noteTop"
            name="noteTop"
            v-validate.disable="'max:100000'"
            :placeholder="$t('note_top')"
            :additional-box-text="$t('or_use_template')"
            @clicked="$refs.topTemplateModal.open()"
            class="q-mt-xl"
            useMarkdown
          />

          <line-items
            v-for="(entry, index) in items[0].children"
            :key="index"
            :value="entry"
            :currency="currency"
            :show-heading-input="items[0].children.length > 1"
            :show-delete-button="items[0].children.length > 1"
            ref="itemsTable"
            class="q-mt-xl"
            @deleteBlock="handleDeleteBlock(index)"
            @deleteItem="(itemIndex) => handleDeleteItem(index, itemIndex)"
            @updateGroup="(value) => handleUpdateGroup(index, value)"
            @updateGroupHeading="(value) => handleUpdateGroupHeading(index, value)"
            @update="handleLineItemUpdate"
          />

          <div class="flex justify-center q-mt-lg">
            <q-btn-dropdown
              v-if="!showItemForm"
              :label="$t('new_line_item_button')"
              color="primary"
              split
              outline
              @click="handleAddItemButton"
            >
              <q-list link>
                <q-item @click.native="handleAddLineItemsBlock" v-close-overlay>
                  <q-item-main>
                    <q-item-tile label>{{ $t('invoice_block_add') }}</q-item-tile>
                  </q-item-main>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>

          <items-list-new-entry
            v-if="showItemForm"
            :currency="currency"
            :active-line-item="activeLineItem"
            type="quote"
            class="q-mt-lg"
            @add="handleAddItem"
            @update="handleUpdateItem"
            @cancel="handleItemFormCancel"
          />

          <div v-if="errors.has('items')" class="on-right">
            <span class="text-negative">{{ $t('item_required') }}</span>
          </div>

          <invoice-price-summary
            :items="lineItems"
            :discount="discount"
            :cashDiscount="cashDiscount"
            class="q-mt-xl"
          />

          <div class="row justify-end q-mt-sm q-mb-xl">
            <q-btn
              v-if="discount === null"
              color="grey-7"
              flat
              outline
              dense
              no-caps
              @click="addDiscount"
            >
              {{ $t('add_discount') }}
            </q-btn>

            <q-btn
              v-else
              color="negative"
              flat
              outline
              dense
              no-caps
              @click="deleteDiscount"
            >
              {{ $t('delete_discount') }}
            </q-btn>
          </div>

          <add-textarea-box
            v-model="noteBottom"
            name="noteBottom"
            v-validate.disable="'max:100000'"
            :placeholder="$t('note_bottom')"
            :additional-box-text="$t('or_use_template')"
            @clicked="$refs.bottomTemplateModal.open()"
            class="q-mt-xl"
            useMarkdown
          />
        </main>

        <div class="footer">
          <q-collapsible :label="$t('options')">
            <!-- Language -->
            <div class="row">
              <div class="col-12 col-md-6">
                <language-input
                  v-model="language"
                  :labelWidth="3"
                />
              </div>
            </div>
          </q-collapsible>
        </div>
      </section>
    </div>

    <text-template-modal
      ref="topTemplateModal"
      model="quote"
      @selected="text => this.noteTop = text"
      initialPosition="top"
    />
    <text-template-modal
      ref="bottomTemplateModal"
      model="quote"
      @selected="text => this.noteBottom = text"
      initialPosition="bottom"
    />
    <add-discount-dialog ref="addDiscountDialog" :currency="currency" @discount="handleAddDiscount"/>
    <preview-modal ref="previewModal" :blob="previewBlob"/>
  </div>
</template>

<script>
  import { Notify, date } from 'quasar'
  import { dateIsEligibleForVatDiscount, itemsContainDiscountVatRate } from 'src/utils/vatDiscount'
  import { mapActions, mapGetters } from 'vuex'
  import AddDiscountDialog from 'components/Invoices/Create/AddDiscountDialog.vue'
  import AddTextareaBox from 'components/Invoices/Create/AddTextareaBox.vue'
  import CustomerAddressSelect from 'components/Quotes/Create/CustomerAddressSelect.vue'
  import CustomerSelect from 'components/Invoices/Create/CustomerSelect.vue'
  import { CustomersClient } from 'src/api/Customers'
  import InvoicePriceSummary from 'components/Invoices/Create/InvoicePriceSummary.vue'
  import ItemsListNewEntry from 'components/Invoices/Create/ItemForm.vue'
  import LanguageInput from 'components/Common/Inputs/LanguageInput.vue'
  import LineItems from 'components/Invoices/Create/LineItems.vue'
  import PreviewModal from 'components/Invoices/Create/PreviewModal.vue'
  import ProjectIndicator from 'components/TimeTracking/ProjectIndicator.vue'
  import ProjectPopover from 'components/TimeTracking/ProjectPopover.vue'
  import { ProjectsClient } from 'src/api/Projects'
  import { QuoteClient } from 'src/api/Quotes'
  import TextTemplateModal from 'components/TextTemplates/TextTemplateModal.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { customerTypes } from 'src/utils/customerTypes'
  import { general } from 'src/config'
  import i18n from 'src/i18n/i18n'
  import parseDate from 'date-fns/parse'
  import store from 'src/store/index'

  function replaceDecimalSign (value) {
    if (typeof value === 'string') {
      value = value.replace(/,/, '.')
    }

    return value
  }

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        activeQuote: null,
        activeItemIdx: null,
        invoiceQuote: null,
        editTitle: false,
        customer: null,
        contactPerson: null,
        address: null,
        quoteNumber: null,
        quoteDate: null,
        validUntil: null,
        noteTop: '',
        noteBottom: '',
        currency: general.base_currency,
        discount: null,
        cashDiscount: null,
        language: general.default_locale,
        quoteSaved: false,
        quoteTitle: i18n.tc('quote'),
        project: null,
        previewBlob: null
      }
    },

    computed: {
      ...mapGetters(['activeTeam', 'autoGenerateQuoteNumber']),
      ...mapGetters('createQuote', ['items', 'showItemForm', 'activeLineItem']),

      editing () {
        return this.activeQuote !== null
      },

      editingDraft () {
        return this.editing && this.activeQuote.status === 'draft'
      },

      nextQuoteNumber () {
        return `#${this.activeTeam.settings.current_quote_number_formatted}`
      },

      teamAddress () {
        return this.activeTeam.settings.default_address
      },

      returnLine () {
        if (!this.teamAddress) {
          return ''
        }

        return [
          this.activeTeam.name,
          this.teamAddress.address_line_1,
          `${this.teamAddress.postal_code}&nbsp;${this.teamAddress.locality}`
        ]
          .join('&nbsp;|&nbsp;')
      },

      lineItems () {
        return this.items
          .filter(block => block.type === 'table')
          .flatMap(table => table.children)
          .flatMap(group => group.children)
      },

      mappedItems () {
        return this.items.map(block => {
          if (block.type !== 'table') {
            return block
          }

          return {
            ...block,
            children: block.children.map(group => ({
              ...group,
              children: group.children.map(lineItem => ({
                id: lineItem.id,
                item_name: lineItem.itemName,
                price: lineItem.price,
                quantity: replaceDecimalSign(lineItem.quantity),
                sales_tax: lineItem.salesTax,
                description: lineItem.description,
                item_number: lineItem.itemNumber,
                discount_absolute: lineItem.discountAbsolute,
                discount_relative: lineItem.discountRelative,
                unit_name: lineItem.unitName
              }))
            }))
          }
        })
      },
      customerId () {
        return this.customer ? this.customer.id : null
      },
      customerTypes () {
        return customerTypes
      },
      generateQuoteNumber: {
        get () {
          return this.autoGenerateQuoteNumber && (this.quoteNumber === '' || this.quoteNumber === null)
        },

        set (value) {
          this.setAutoGenerateQuoteNumber(value)
        }
      }
    },

    methods: {
      ...mapActions(['setAutoGenerateQuoteNumber']),
      ...mapActions('createQuote', [
        'loadItems',
        'addLineItemsBlock',
        'deleteBlock',
        'addLineItem',
        'deleteLineItem',
        'updateActiveLineItem',
        'clearActiveLineItem',
        'showCreateItemForm',
        'cancelItemForm',
        'updateGroup',
        'updateGroupHeading',
        'setActiveLineItem',
        'resetState'
      ]),

      addDiscount () {
        this.$refs.addDiscountDialog.open()
      },

      deleteDiscount () {
        this.discount = null
      },

      handleAddDiscount (discount) {
        this.discount = discount
      },

      checkQuoteNumber () {
        if (this.quoteNumber === '' || this.quoteNumber === null) {
          this.quoteNumber = null
          this.generateQuoteNumber = true
        }
      },

      handleBlurQuoteTitle () {
        if (this.quoteTitle === '' || this.quoteTitle === null) {
          this.quoteTitle = this.$tc('quote', 1)
        }

        this.editTitle = false
      },

      toggleQuoteTitle () {
        this.editTitle = true
        this.$nextTick(() => {
          this.$refs.quoteTitleInput.focus()
        })
      },

      toggleQuoteNumber () {
        this.generateQuoteNumber = false
        this.$nextTick(() => {
          this.$refs.quoteNumberInput.focus()
        })
      },

      handleSelectProject (project) {
        this.project = project
      },

      handleCustomerSelect (customer) {
        if (this.errors) {
          this.errors.remove('customer')
          this.errors.remove('address')
        }
        this.updateCustomer(customer)
      },

      prepareData () {
        let data = {
          'team_address_id': this.activeTeam.settings.default_address.id,
          'customer_id': this.customer.id,
          'contact_person_id': this.contactPerson ? this.contactPerson.id : null,
          'address_id': this.address.id,
          'items': this.mappedItems,
          'invoice_number': this.invoiceNumber,
          'quote_date': date.formatDate(this.quoteDate, 'YYYY-MM-DD'),
          'valid_until': date.formatDate(this.validUntil, 'YYYY-MM-DD'),
          'quote_title': this.quoteTitle,
          'quote_number': this.quoteNumber,
          'note_top': this.noteTop ? this.noteTop.replaceAll('</div>', '').replaceAll('<div>', '<br />') : null, // Fix for QEditor not supporting paragraphs
          'note_bottom': this.noteBottom ? this.noteBottom.replaceAll('</div>', '').replaceAll('<div>', '<br />') : null, // Fix for QEditor not supporting paragraphs
          'currency': this.currency,
          'project_id': this.project ? this.project.id : null,
          'discount_relative': null,
          'discount_absolute': null,
          'language': this.language
        }

        if (this.discount !== null) {
          const attributeName = this.discount.type === 'rel' ? 'discount_relative' : 'discount_absolute'
          data[attributeName] = this.discount.type === 'rel' ? replaceDecimalSign(this.discount.value) : this.discount.value
        }

        return Promise.resolve(data)
      },

      saveDraft () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              this.$q.notify({ message: this.$t('please_resolve_all_validation_errors'), type: 'warning' })
              throw new Error('validation')
            }

            return this.prepareData()
          })
          .then(data => {
            data.status = 'draft'

            const client = new QuoteClient()
            return this.activeQuote
              ? client.update(this.activeQuote.id, data)
              : client.include(['customer.addresses', 'items', 'project', 'project.customer']).create(data)
          })
          .then(response => {
            const quote = response.data.data
            this.quoteSaved = true
            this.$q.notify({ message: this.$t('quote_draft_saved'), type: 'positive' })

            if (!this.activeQuote) {
              this.activeQuote = quote
            }
            this.$router.push({ name: 'edit-quote', params: { quote: quote.id, teamId: quote.team_id } })
          })
          .catch(error => {
            if (error.message !== 'validation') {
              createErrorFromResponse(error)
            }
          })
      },

      createQuote () {
        if (!dateIsEligibleForVatDiscount(this.quoteDate) && itemsContainDiscountVatRate(this.lineItems)) {
          this.$q.dialog({
            title: this.$t('vat'),
            message: this.$t('vat_reduction'),
            ok: this.$t('save'),
            cancel: this.$t('edit')
          }).then(this.storeQuote)
            .catch(() => {
            })
        } else {
          return this.storeQuote()
        }
      },

      storeQuote () {
        const client = new QuoteClient()

        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              this.$q.notify({ message: this.$t('please_resolve_all_validation_errors'), type: 'warning' })
              throw new Error('validation')
            }

            return this.prepareData()
          })
          .then(data => {
            data.status = 'open'

            return this.activeQuote
              ? client.update(this.activeQuote.id, data)
              : client.create(data)
          })
          .then(response => {
            return this.activeQuote
              ? client.setStatus(this.activeQuote.id, { status_code: 'open' })
              : Promise.resolve(response)
          })
          .then(response => {
            this.quoteSaved = true
            const quote = this.activeQuote || response.data.data

            this.$q.notify({ message: this.$t('quote_created'), type: 'positive' })
            this.$router.push({
              name: 'quote',
              params: { quote: quote.id, teamId: this.activeTeam.id }
            })
          })
          .catch(error => {
            if (error.message !== 'validation') {
              createErrorFromResponse(error)
            }
          })
      },

      previewQuote () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              this.$q.notify({ message: this.$t('please_resolve_all_validation_errors'), type: 'warning' })
              throw new Error('validation')
            }

            return this.prepareData()
          })
          .then(data => {
            return new QuoteClient()
              .pdfPreview(data)
          })
          .then(response => {
            const pdf = response.data
            const blob = new Blob([pdf], { type: 'application/pdf' })
            this.previewBlob = blob
            this.$refs.previewModal.open()
          })
          .catch(error => {
            if (error.message !== 'validation') {
              createErrorFromResponse('validation')
            }
          })
      },

      handleReFetchCustomer () {
        new CustomersClient()
          .include(['addresses'])
          .get(this.customer.id)
          .then(response => {
            this.customer = response.data.data[0]
          })

        if (this.errors) {
          this.errors.remove('address')
        }
      },

      setDefaults () {
        this.quoteDate = new Date()
        this.validUntil = date.addToDate(new Date(), { days: this.activeTeam.settings.default_quote_deadline })
        this.language = this.language ? this.language : this.activeTeam.settings.language
      },

      itemsFromResponse (items) {
        return items.map(block => {
          if (block.type !== 'table') {
            return block
          }

          return {
            ...block,
            children: block.children.map(group => ({
              ...group,
              children: group.children.map(lineItem => ({
                ...lineItem,
                id: lineItem.id,
                itemName: lineItem.item_name,
                price: lineItem.price,
                quantity: lineItem.quantity,
                salesTax: lineItem.sales_tax,
                description: lineItem.description,
                itemNumber: lineItem.item_number,
                discountAbsolute: lineItem.discount_absolute,
                discountRelative: lineItem.discount_relative,
                unitName: lineItem.unit_name
              }))
            }))
          }
        })
      },

      loadQuote (quote) {
        this.quoteNumber = quote.quote_number
        this.quoteTitle = quote.quote_title
        this.quoteDate = parseDate(quote.quote_date.date)
        this.validUntil = quote.valid_until ? parseDate(quote.valid_until.date) : null
        this.noteTop = quote.note_top
        this.noteBottom = quote.note_bottom
        this.currency = quote.currency
        this.customer = quote.customer ? quote.customer.data : null
        this.contactPerson = quote.contact_person ? quote.contact_person.data : null
        this.invoice = quote.invoice_id
        this.language = quote.language
        this.project = quote.project ? quote.project.data : null

        if (quote.discount_absolute) {
          this.discount = {
            value: quote.discount_absolute,
            type: 'abs'
          }
        } else if (quote.discount_relative) {
          this.discount = {
            value: quote.discount_relative,
            type: 'rel'
          }
        }

        if (quote.address_id) {
          this.address = quote.customer.data.addresses.data.find(address => address.id === quote.address_id)
        }

        this.loadItems(quote.items.data)
      },

      handleAddLineItemsBlock () {
        this.addLineItemsBlock()
      },

      handleLineItemInput (data, index) {
        this.items[0].children.splice(index, 1, data)
      },

      handleDeleteBlock (index) {
        this.deleteBlock(index)
      },

      handleAddItemButton () {
        this.showCreateItemForm()
      },

      handleAddItem (item) {
        this.addLineItem(item)
        this.cancelItemForm()
        this.clearActiveLineItem()
      },

      handleUpdateItem (item) {
        this.updateActiveLineItem(item)
        this.cancelItemForm()
        this.clearActiveLineItem()
      },

      handleDeleteItem (groupIndex, itemIndex) {
        this.deleteLineItem({ groupIndex, itemIndex })
      },

      handleItemFormCancel () {
        this.cancelItemForm()
        this.clearActiveLineItem()
      },

      handleUpdateGroup (groupIndex, value) {
        this.updateGroup({ groupIndex, value })
      },

      handleUpdateGroupHeading (groupIndex, heading) {
        this.updateGroupHeading({ groupIndex, heading })
      },

      handleLineItemUpdate (item) {
        this.setActiveLineItem(item)
        this.showCreateItemForm()
      },
      updateCustomer (customer) {
        if (customer === null) {
          this.customer = null
          this.contactPerson = null
        } else if (customer.type === 'contact_person') {
          this.customer = customer.company.data
          this.customer.type = 'company'
          this.contactPerson = customer
        } else if (this.contactPerson &&
          this.contactPerson.company &&
          this.contactPerson.company.data.id !== customer.id) {
            this.customer = customer
            this.contactPerson = null
        } else {
          this.customer = customer
        }
      }
    },

    created () {
      this.$validator.attach({
        name: 'customer',
        rules: 'required',
        getter: () => {
          return this.customer
        },
        alias: this.$tc('customer', 1)
      })
      this.$validator.attach({
        name: 'address',
        rules: 'required',
        getter: () => {
          return this.address
        },
        alias: this.$t('address')
      })
      this.$validator.attach({
        name: 'items',
        rules: 'required|invoicing_items',
        getter: () => {
          return this.items
        }
      })

      this.setDefaults()
    },

    watch: {
      activeQuote (quote) {
        this.loadQuote(quote)
      }
    },

    beforeRouteEnter (to, from, next) {
      if (from.name === 'create-quote') {
        next()

        return
      }

      store.dispatch('createQuote/resetState')
      store.dispatch('refreshTeamData')
      store.dispatch('listQuoteTextTemplates', { pagination: { sortBy: 'id', descending: true } })

      if (to.params.hasOwnProperty('quote')) {
        const quoteId = to.params.quote

        new QuoteClient()
          .include(['customer.addresses', 'items', 'project', 'contact_person'])
          .get(quoteId)
          .then(response => {
            next(vm => {
              vm.activeQuote = response.data.data[0]
            })
          })
          .catch(() => {
            Notify.create({ message: i18n.t('quote_not_found_notification'), type: 'warning' })
            next({ name: 'create-quote', params: { teamId: store.getters.activeTeam.id } })
          })
      } else if (to.query.hasOwnProperty('customer')) {
        const customerId = to.query.customer

        new CustomersClient()
          .include(['addresses'])
          .get(customerId)
          .then(response => next(vm => {
            vm.customer = response.data.data[0]
          }))
          .catch(() => {
            Notify.create({ message: i18n.t('partner_not_found_notification'), type: 'warning' })
            next({ name: 'create-quote', params: { teamId: store.getters.activeTeam.id } })
          })
      } else if (to.query.hasOwnProperty('copy')) {
        const quoteId = to.query.copy

        new QuoteClient()
          .include(['customer.addresses', 'items', 'project', 'project.customer', 'contact_person'])
          .get(quoteId)
          .then(response => {
            next(vm => {
              const quote = response.data.data[0]
              const customer = quote.customer ? quote.customer.data : null
              const customerHasAddress = (customer, addressId) => {
                return customer.addresses.data.find(address => address.id === addressId)
              }

              quote.quote_number = null
              quote.validUntil = null

              if (!quote.customer_exists) {
                quote.customer = null
                quote.customer_id = null
                quote.customer_exists = null

                quote.address_id = null
              } else if (customer && !customer.addresses) {
                quote.address_id = null
              } else if (customer && customer.addresses) {
                if (quote.address_id && !customerHasAddress(customer, quote.address_id)) {
                  quote.address_id = null
                }
              }

              if (!quote.contact_person_exists) {
                quote.contact_person_id = null
                quote.contact_person = null
              }

              vm.loadQuote(quote)
              vm.setDefaults()
            })
          })
          .catch(() => {
            Notify.create({ message: i18n.t('quote_not_found_notification'), type: 'warning' })
            next({ name: 'create-quote', params: { teamId: store.getters.activeTeam.id } })
          })
      } else if (to.query.hasOwnProperty('project')) {
        const projectId = to.query.project

        new ProjectsClient()
          .include(['customer', 'customer.addresses'])
          .get(projectId)
          .then(response => {
            return response.data.data[0]
          })
          .then(project => {
            next(vm => {
              if (project.customer) {
                vm.customer = project.customer.data
              }

              vm.project = project
            })
          })
      } else {
        next()
      }
    },

    beforeRouteLeave (to, from, next) {
      if (this.quoteSaved) {
        this.cancelItemForm()
        this.clearActiveLineItem()

        next()
        return
      }

      this.$q.dialog({
        title: this.$t('leave_page_title'),
        message: this.$t('leave_page_message'),
        ok: this.$t('leave_page_ok'),
        cancel: this.$t('leave_page_cancel')
      }).then(() => {
        this.cancelItemForm()
        this.clearActiveLineItem()

        next()
      })
        .catch(() => next(false))
    },

    components: {
      AddTextareaBox,
      InvoicePriceSummary,
      CustomerSelect,
      CustomerAddressSelect,
      LanguageInput,
      LineItems,
      AddDiscountDialog,
      TextTemplateModal,
      ProjectIndicator,
      ProjectPopover,
      ItemsListNewEntry,
      PreviewModal
    }
  }
</script>

<style src="src/components/Invoices/Create/create-invoice.styl" lang="stylus"></style>
